import _ from 'lodash';
import React, {FunctionComponent} from 'react';
import {useSelector} from 'react-redux';
import {Grid, makeStyles} from '@material-ui/core';
import {
    ProductField,
    ProductData,
    ProductAttributesField,
    ProductInCatalog,
} from '@treez/commons/sharedTypings/product';
import {flattenProduct} from '~/lib/product';
import {ColumnWidthOption} from '~/lib/sharedInterfaces';
import {selectMergeProductsStorePresence} from '~/redux/selectors/mergeSelectors';
import ImageGalleryGridCell, {ImageGalleryGridCellOption} from './ImageGalleryGridCell';
import MergeDescriptionCell from './MergeDescriptionCell';
import MergeMultiValueProductGridCell from './MergeMultiValueProductGridCell';
import OwnerTypeCell from './OwnerTypeCell';
import ProductGridCell from './ProductGridCell';
import ProductOverviewCell from './ProductOverviewCell';
import StorePresenceCell from './StorePresenceCell';

interface Props {
    fieldsToShow: ProductData[];
    product: ProductInCatalog;
    mergeToThisProduct: (product: ProductInCatalog) => void;
    removeProduct?: (productId: number) => void;
    columnWidth?: ColumnWidthOption;
}

const {IMAGES} = ProductField;

export const MergeProductColumn: FunctionComponent<Props> = (props: Props) => {
    const {columnWidth, fieldsToShow, mergeToThisProduct, product, removeProduct} = props;

    const mergeProductsStorePresence = useSelector(selectMergeProductsStorePresence, _.isEqual);

    const productId = product.id;

    const productStorePresence = mergeProductsStorePresence[productId];

    const useStyles = makeStyles({
        productColumn: {
            minWidth: '200px',
        },
    });

    const {productColumn} = useStyles();

    const flatProduct = flattenProduct(product);

    const renderProductField = (field: ProductData) => {
        const fieldHasMultipleValues = _.includes(_.values(ProductAttributesField), field);

        if (fieldHasMultipleValues) {
            return (
                <MergeMultiValueProductGridCell
                    field={field}
                    key={`${field}-${product.id}`}
                    values={flatProduct[field]}
                />
            );
        }
        if (field === ProductField.DESCRIPTIONS) {
            const {descriptions} = flatProduct;

            const productDescription = descriptions ? descriptions.main : '';

            return (
                <MergeDescriptionCell
                    clickOrEdit="click"
                    text={productDescription || ''}
                    key={`description-${product.id}`}
                />
            );
        }
        if (field === ProductField.OWNER_TYPE) {
            return (
                <OwnerTypeCell ownerType={product.ownerType} productForm={false} key={product.id} />
            );
        }
        return (
            <ProductGridCell
                disabled={false}
                field={field}
                key={`${field}-${product.id}`}
                product={product}
            />
        );
    };

    const maxNumberOfStoresToShow = 2;

    return (
        <Grid
            item
            container
            alignContent="flex-start"
            className={productColumn}
            direction="column"
            justifyContent="flex-start"
            sm={columnWidth}
        >
            <StorePresenceCell
                productStorePresence={productStorePresence}
                maxNumberOfStoresToShow={maxNumberOfStoresToShow}
            />
            <ImageGalleryGridCell
                editOrSelect={ImageGalleryGridCellOption.SELECT}
                images={product[IMAGES]}
            />
            <ProductOverviewCell
                mergeToThisProduct={mergeToThisProduct}
                ownerType={product.ownerType}
                product={product}
                removeProduct={removeProduct}
            />
            {fieldsToShow.map(renderProductField)}
        </Grid>
    );
};

export default MergeProductColumn;
